import { Navbar } from '@common/ui/navigation/navbar/navbar';
import { useUser } from '../use-user';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { BasicInfoPanel } from './basic-info-panel/basic-info-panel';
import { LocalizationPanel } from './localization-panel';
import { DangerZonePanel } from './danger-zone-panel/danger-zone-panel';
import { Trans } from '@ui/i18n/trans';
import { StaticPageTitle } from '@common/seo/static-page-title';
import {
  AccountSettingsId,
  AccountSettingsMobileNav,
  AccountSettingsSidenav,
} from '@common/auth/ui/account-settings/account-settings-sidenav';
import { SessionsPanel } from '@common/auth/ui/account-settings/sessions-panel/sessions-panel';
import { useContext, useState } from 'react';
import { SiteConfigContext } from '@common/core/settings/site-config-context';
import { TwoFactorPanel } from '@common/auth/ui/account-settings/two-factor-panel';
import { AnimatePresence, motion } from 'framer-motion';
import { SubscriptionPanel } from './subscription-panel/subscription-panel';

export function AccountSettingsPage() {
  const { auth } = useContext(SiteConfigContext);
  const [selectedTab, setSelectedTab] = useState<AccountSettingsId>(
    AccountSettingsId.AccountDetails
  );
  const { data, isLoading } = useUser('me', {
    with: ['roles', 'social_profiles', 'tokens'],
  });
  return (
    <div id="account-settings-page" className="min-h-screen bg-black text-white selection:bg-primary/30">
      <StaticPageTitle>
        <Trans message="Account Settings" />
      </StaticPageTitle>
      <Navbar menuPosition="account-settings-page" />
      <div className="relative isolate overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-0 right-0 -z-10 h-[500px] w-[500px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-10 -left-10 -z-10 h-[400px] w-[400px] rounded-full bg-blue-500/5 blur-[100px]" />

        <div className="container mx-auto px-24 py-48 md:py-64">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
              <Trans message="Settings" />
            </h1>
            <div className="mb-48 mt-12 text-lg text-white/50 max-w-2xl">
              <Trans message="View and update your account details, profile and more." />
            </div>
          </motion.div>

          {isLoading || !data ? (
            <div className="flex h-[400px] items-center justify-center">
              <ProgressCircle
                className="text-primary"
                aria-label="Loading user.."
                isIndeterminate
              />
            </div>
          ) : (
            <div className="flex flex-col gap-40 lg:flex-row lg:items-start lg:gap-64">
              <AccountSettingsMobileNav
                selectedTab={selectedTab}
                onTabChange={setSelectedTab}
              />
              <AccountSettingsSidenav
                selectedTab={selectedTab}
                onTabChange={setSelectedTab}
              />
              <div className="flex-auto max-w-4xl min-h-[500px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedTab}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    {auth.accountSettingsPanels
                      ?.filter(p => p.id === selectedTab)
                      .map(panel => (
                        <panel.component key={panel.id} user={data.user} />
                      ))}
                    {selectedTab === AccountSettingsId.AccountDetails && (
                      <BasicInfoPanel user={data.user} />
                    )}
                    {selectedTab === AccountSettingsId.TwoFactor && (
                      <TwoFactorPanel user={data.user} />
                    )}
                    {selectedTab === AccountSettingsId.Sessions && (
                      <SessionsPanel />
                    )}
                    {selectedTab === AccountSettingsId.LocationAndLanguage && (
                      <LocalizationPanel user={data.user} />
                    )}
                    {selectedTab === AccountSettingsId.Subscription && (
                      <SubscriptionPanel />
                    )}
                    {selectedTab === AccountSettingsId.DeleteAccount && (
                      <DangerZonePanel />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
